import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import type { TherapyCaseloadView } from "@alm/validation";
import { PageHeader } from "@/components/app/shell/page-header";
import { ListToolbar } from "@/components/app/patterns/list-toolbar";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { ReportExport } from "@/components/app/documents/report-export";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import {
  CaseloadFilters,
  NO_CASELOAD_FILTERS,
  type CaseloadFilterState,
} from "@/routes/_authenticated/_care/therapy-caseload/-components/caseload-filters";
import { ComplianceView } from "@/routes/_authenticated/_care/therapy-caseload/-components/compliance-view";
import { ProductivityView } from "@/routes/_authenticated/_care/therapy-caseload/-components/productivity-view";
import { RosterView } from "@/routes/_authenticated/_care/therapy-caseload/-components/roster-view";
import { SignatureView } from "@/routes/_authenticated/_care/therapy-caseload/-components/signature-view";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { saveFile } from "@/lib/files/download";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { optionalInt, optionalOneOf, stringParams, KEEP_PLACE, optionalSearch } from "@/lib/search";
import { VisitsView } from "./-components/visits-view";
import { SearchField } from "@/components/app/patterns/search-field";

type View = TherapyCaseloadView | "visits";
const VIEW_PARAMS = ["visits", "compliance", "signature", "productivity"] as const;

type CaseloadSearch = {
  view?: (typeof VIEW_PARAMS)[number];
  q?: string;
  discipline?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  window?: number;
};

export const Route = createFileRoute("/_authenticated/_care/therapy-caseload/")({
  validateSearch: (search: Record<string, unknown>): CaseloadSearch => ({
    view: optionalOneOf(VIEW_PARAMS, search.view),
    q: optionalSearch(search.q),
    ...stringParams(["discipline", "status", "startDate", "endDate"], search),
    window: optionalInt(search.window, 1, 365),
  }),
  component: Caseload,
});

/* "visits" is not a `TherapyCaseloadView`: the other four are cuts of one
   report and this one has its own procedure, because it answers about the
   notes rather than about the caseload. */
const VIEWS: ReadonlyArray<{ value: View; label: string }> = [
  { value: "roster", label: "Active roster" },
  { value: "visits", label: "Visits" },
  { value: "compliance", label: "Compliance" },
  { value: "signature", label: "Signatures" },
  { value: "productivity", label: "Productivity" },
];

/** One report, one filter set, four views: downloads carry the active filters so they match the
 * screen. */
function Caseload() {
  const { facilityId } = useFacility();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<CaseloadSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const view: View = params.view ?? "roster";
  const setView = (next: View) => patch({ view: optionalOneOf(VIEW_PARAMS, next) });
  const filters: CaseloadFilterState = {
    discipline: params.discipline ?? NO_CASELOAD_FILTERS.discipline,
    status: params.status ?? NO_CASELOAD_FILTERS.status,
    recertWindowDays: params.window ?? NO_CASELOAD_FILTERS.recertWindowDays,
    startDate: params.startDate ?? "",
    endDate: params.endDate ?? "",
  };
  const setFilters = (next: CaseloadFilterState) =>
    patch({
      discipline: next.discipline === NO_CASELOAD_FILTERS.discipline ? undefined : next.discipline,
      status: next.status === NO_CASELOAD_FILTERS.status ? undefined : next.status,
      window:
        next.recertWindowDays === NO_CASELOAD_FILTERS.recertWindowDays
          ? undefined
          : next.recertWindowDays,
      startDate: next.startDate || undefined,
      endDate: next.endDate || undefined,
    });
  /* The name search is a refinement of what is on screen, not part of the
     server filter, so a download carries the rows the reader is looking at. */
  const { text: search, change: setSearch } = useSearchParamText(params.q, (q) => patch({ q }));

  const input = {
    facilityId,
    recertWindowDays: filters.recertWindowDays,
    disciplines: filters.discipline === "all" ? undefined : [filters.discipline],
    statuses: filters.status === "all" ? undefined : [filters.status as "active" | "on_hold"],
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
  };
  const report = useQuery(orpc.care.therapyCaseload.report.queryOptions({ input }));

  /* The caseload export serves the four cuts of its own report. The visits
     view has its own table and its own CSV, so the toolbar's export falls back
     to the roster rather than asking for a view the exporter does not know. */
  const exportView: TherapyCaseloadView = view === "visits" ? "roster" : view;
  const download = useMutation({
    mutationFn: (format: "csv" | "pdf") =>
      format === "csv"
        ? orpc.care.therapyCaseload.exportCsv.call({ ...input, view: exportView })
        : orpc.care.therapyCaseload.exportPdf.call({ ...input, view: exportView }),
    onSuccess: saveFile,
    onError: (error: Error) => toast.error(errorWords(error)),
  });

  const term = search.trim().toLowerCase();
  const named = <T extends { residentName: string }>(rows: readonly T[] | undefined) =>
    !rows || !term ? rows : rows.filter((row) => row.residentName.toLowerCase().includes(term));

  const compliance = report.data?.compliance ?? [];
  const overdue = compliance.filter((row) => row.severity === "overdue").length;
  const dueSoon = compliance.filter((row) => row.severity === "due_soon").length;
  const active = report.data?.roster.length ?? 0;
  const backlog = report.data?.signatureBacklog.length ?? 0;
  /* A zero before the report arrives, or after it failed, reads as "none". */
  const tile = (value: number) => (report.data ? value : "-");

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Therapy caseload"
        count={report.data ? active : undefined}
        meta={
          report.isPending || report.isError
            ? undefined
            : `${active} active ${active === 1 ? "episode" : "episodes"}${overdue ? `, ${overdue} overdue` : ""}${backlog ? `, ${backlog} awaiting signature` : ""}`
        }
        actions={
          <ReportExport
            labels={{ csv: "Report CSV", pdf: "Print report" }}
            pending={download.isPending}
            onExport={(format) => download.mutate(format)}
          />
        }
      />

      <StatTiles className="xl:grid-cols-4">
        <StatTile label="Active episodes" value={tile(active)} hint="Open or on hold" />
        <StatTile label="Overdue" value={tile(overdue)} hint="Recerts and discharges" />
        <StatTile
          label="Due soon"
          value={tile(dueSoon)}
          hint={`Within ${filters.recertWindowDays} days`}
        />
        <StatTile label="Awaiting signature" value={tile(backlog)} hint="Notes" />
      </StatTiles>

      <ToggleGroup
        type="single"
        value={view}
        variant="outline"
        onValueChange={(next) => next && setView(next as View)}
      >
        {VIEWS.map((option) => (
          <ToggleGroupItem key={option.value} value={option.value}>
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      <ListToolbar
        search={
          view === "visits" ? null : (
            <SearchField
              placeholder="Search resident"
              value={search}
              onChange={setSearch}
              className="sm:w-full"
            />
          )
        }
        trailing={
          <CaseloadFilters
            filters={filters}
            showStatus={view === "roster"}
            showDates={view === "productivity" || view === "visits"}
            onChange={setFilters}
          />
        }
      />

      {report.isError && view !== "visits" ? (
        <ReadFailed
          message="Could not load the caseload report."
          error={report.error}
          onRetry={() => void report.refetch()}
          retrying={report.isFetching}
        />
      ) : null}
      {view === "roster" && !report.isError ? (
        <RosterView rows={named(report.data?.roster)} pending={report.isPending} />
      ) : null}
      {view === "compliance" && !report.isError ? (
        <ComplianceView rows={named(report.data?.compliance)} pending={report.isPending} />
      ) : null}
      {view === "signature" && !report.isError ? (
        <SignatureView rows={named(report.data?.signatureBacklog)} pending={report.isPending} />
      ) : null}
      {view === "productivity" && !report.isError ? (
        <ProductivityView productivity={report.data?.productivity} pending={report.isPending} />
      ) : null}
      {view === "visits" ? (
        <VisitsView
          facilityId={facilityId}
          startDate={filters.startDate || undefined}
          endDate={filters.endDate || undefined}
          discipline={filters.discipline === "all" ? undefined : filters.discipline}
        />
      ) : null}
    </div>
  );
}
